import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import soundtrack from "./assets/soundtrack.mp3";

// ─── Constants ────────────────────────────────────────────────────────────────
const BASE_COLS = 10;
const BASE_ROWS = 4;
const INVADER_W = 0.7;
const INVADER_H = 0.5;
const INVADER_GAP_X = 1.1;
const INVADER_GAP_Y = 0.9;
const INVADER_SPIN_SPEED = 0.9;
const PLAYER_SPEED = 6;
const BULLET_SPEED = 14;
const ENEMY_BULLET_SPEED = 6;
const SHOOT_COOLDOWN = 0.35;
const INVADER_DROP = 0.4;
const INITIAL_INVADER_SPEED = 1.2;
const STAR_COUNT = 180;

// ─── Color palette ────────────────────────────────────────────────────────────
const COLORS = {
  bg: 0x000510,
  player: 0x00ffe0,
  playerGlow: 0x00ffe0,
  bulletPlayer: 0x00ffe0,
  bulletEnemy: 0xff3c6e,
  invaderA: 0xff3c6e,
  invaderB: 0xffb800,
  invaderC: 0xb44fff,
  shield: 0x00ff88,
  star: 0xffffff,
  explosion: 0xff6a00,
  hud: "#00ffe0",
  hudAlt: "#ff3c6e",
};

const INVADER_PALETTES = [
  {
    invaderA: 0xff00d6,
    invaderB: 0x00f5ff,
    invaderC: 0xb600ff,
    glow: 0xff5dff,
    eye: 0x050017,
  },
  {
    invaderA: 0x00d9ff,
    invaderB: 0xffb300,
    invaderC: 0xff4d00,
    glow: 0x00e9ff,
    eye: 0x070012,
  },
  {
    invaderA: 0xb100ff,
    invaderB: 0x5dff00,
    invaderC: 0xff6a00,
    glow: 0xc600ff,
    eye: 0x060013,
  },
  {
    invaderA: 0xff007a,
    invaderB: 0x00f0ff,
    invaderC: 0x6b60ff,
    glow: 0xff1aa3,
    eye: 0x050016,
  },
];

// Returns the synthwave invader palette for the current level, cycling as needed.
function getInvaderPaletteForLevel(level) {
  const index = Math.max(0, level - 1) % INVADER_PALETTES.length;
  return INVADER_PALETTES[index];
}

// Returns a random number within the provided range.
function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

// Computes responsive gameplay settings based on the current screen width.
function getResponsiveSettings(width) {
  const normalized = Math.min(1, Math.max(0.5, width / 520));
  const speedScale = normalized * normalized * normalized;
  const isPhoneWidth = width <= 420;
  const enemyScale = isPhoneWidth ? 0.5 : normalized;
  const cols = Math.max(5, Math.round(BASE_COLS * enemyScale));
  const rows = Math.max(2, Math.round(BASE_ROWS * enemyScale));
  const shieldCount = Math.max(2, Math.round(4 * enemyScale));
  const invaderDropScale = speedScale;
  const invaderStepScale = speedScale;
  const invaderIntervalScale = 1 / speedScale;
  return { cols, rows, shieldCount, invaderDropScale, invaderStepScale, invaderIntervalScale };
}

function createSfxPlayer() {
  // Create and manage simple synthesized sound effects for gameplay events.
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return {
      // Ignore sound effect requests when Web Audio is unavailable.
      playEnemyKill: () => {},
      // Ignore sound effect requests when Web Audio is unavailable.
      playPlayerHit: () => {},
      // Ignore resume requests when Web Audio is unavailable.
      resume: () => Promise.resolve(),
      // Ignore dispose requests when Web Audio is unavailable.
      dispose: () => {},
    };
  }

  const context = new AudioContextClass();
  const master = context.createGain();
  master.gain.value = 0.18;
  master.connect(context.destination);

  const playTone = ({
    type,
    startFreq,
    endFreq,
    duration,
    volume,
    startTime = context.currentTime,
  }) => {
    // Play a single pitched tone with a quick envelope and optional frequency slide.
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, startTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), startTime + duration);
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  };

  const playEnemyKill = () => {
    // Play a sharp burst to signal an invader explosion.
    playTone({ type: "sawtooth", startFreq: 620, endFreq: 160, duration: 0.16, volume: 0.2 });
  };

  const playPlayerHit = () => {
    // Play a heavier descending tone to signal the player taking a hit.
    playTone({ type: "square", startFreq: 260, endFreq: 110, duration: 0.2, volume: 0.22 });
  };

  const resume = () => {
    // Resume the audio context after a user gesture if it is suspended.
    return context.state === "suspended" ? context.resume() : Promise.resolve();
  };

  const dispose = () => {
    // Close the audio context to release resources when the game unmounts.
    if (context.state !== "closed") {
      context.close();
    }
  };

  return {
    playEnemyKill,
    playPlayerHit,
    resume,
    dispose,
  };
}

export default function SpaceGame() {
  const mountRef = useRef(null);
  const gameRef = useRef(null);
  const animRef = useRef(null);
  const touchRef = useRef({ startX: null, startY: null, touchId: null });
  const audioRef = useRef(null);
  const sfxRef = useRef(null);
  const isAllMutedRef = useRef(false);
  const hasInteractedRef = useRef(false);
  const autoFireRef = useRef(true);

  const [gameState, setGameState] = useState("idle"); // idle | playing | paused | gameover | win
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [hiScore, setHiScore] = useState(0);
  const [isSoundtrackMuted, setIsSoundtrackMuted] = useState(false);
  const [isAllMuted, setIsAllMuted] = useState(false);
  const [autoFireEnabled, setAutoFireEnabled] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showDeathMessage, setShowDeathMessage] = useState(false);
  const deathMessageTimerRef = useRef(null);

  useEffect(() => {
    isAllMutedRef.current = isAllMuted;
  }, [isAllMuted]);

  useEffect(() => {
    hasInteractedRef.current = hasUserInteracted;
  }, [hasUserInteracted]);

  useEffect(() => {
    autoFireRef.current = autoFireEnabled;
  }, [autoFireEnabled]);

  // Updates the auto-fire toggle state from checkbox controls.
  const handleAutoFireToggle = useCallback((event) => {
    setAutoFireEnabled(event.target.checked);
  }, []);

  // Creates or reuses the Web Audio helper for short sound effects.
  const getOrCreateSfxPlayer = useCallback(() => {
    if (!sfxRef.current) {
      sfxRef.current = createSfxPlayer();
    }
    return sfxRef.current;
  }, []);

  // Plays a short synthesized sound effect for the provided event if audio is allowed.
  const playSfxEvent = useCallback((kind) => {
    if (isAllMutedRef.current || !hasInteractedRef.current) return;
    const player = getOrCreateSfxPlayer();
    const resumePromise = player.resume?.();
    if (resumePromise?.catch) {
      resumePromise.catch(() => {});
    }
    if (kind === "playerHit") {
      player.playPlayerHit?.();
    } else {
      player.playEnemyKill?.();
    }
  }, [getOrCreateSfxPlayer]);

  // Expose setters to the game loop via ref
  const uiRef = useRef({ score: 0, lives: 3, level: 1, hiScore: 0 });

  // Creates and stores the looping soundtrack element for the game.
  useEffect(() => {
    const audio = new Audio(soundtrack);
    audio.loop = true;
    audio.volume = 0.5;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, []);

  // Ensures the soundtrack reacts to state changes and user interaction.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = isSoundtrackMuted || isAllMuted;
    if (gameState === "playing" && hasUserInteracted) {
      audio.play().catch(() => {});
      return;
    }
    audio.pause();
  }, [gameState, hasUserInteracted, isSoundtrackMuted, isAllMuted]);

  // Marks that the user has interacted so audio playback is allowed.
  const markUserInteracted = useCallback(() => {
    setHasUserInteracted(true);
    const player = getOrCreateSfxPlayer();
    const resumePromise = player.resume?.();
    if (resumePromise?.catch) {
      resumePromise.catch(() => {});
    }
  }, [getOrCreateSfxPlayer]);

  // Toggles mute state for only the game soundtrack.
  const toggleSoundtrackMute = useCallback(() => {
    setIsSoundtrackMuted((prev) => !prev);
  }, []);

  // Toggles mute state for all game audio (soundtrack and sound effects).
  const toggleAllMute = useCallback(() => {
    setIsAllMuted((prev) => !prev);
  }, []);

  // Shows the "you're dead!" overlay briefly when the player loses a life.
  const triggerDeathMessage = useCallback(() => {
    setShowDeathMessage(true);
    if (deathMessageTimerRef.current) {
      clearTimeout(deathMessageTimerRef.current);
    }
    deathMessageTimerRef.current = setTimeout(() => {
      setShowDeathMessage(false);
    }, 1000);
  }, []);

  // ─── Init Three.js scene ───────────────────────────────────────────────────
  const initScene = useCallback(({ lvl = 1, sc = 0, lv = 3 } = {}) => {
    const container = mountRef.current;
    if (!container) return;

    // Clean previous
    if (gameRef.current) {
      gameRef.current.renderer.dispose();
      while (container.firstChild) container.removeChild(container.firstChild);
    }

    const W = container.clientWidth;
    const H = container.clientHeight;
    const aspect = W / H;
    const responsive = getResponsiveSettings(W);

    // Camera: orthographic for classic 2-D feel
    const viewH = 18;
    const viewW = viewH * aspect;
    const camera = new THREE.OrthographicCamera(
      -viewW / 2, viewW / 2,
      viewH / 2, -viewH / 2,
      0.1, 100
    );
    camera.position.z = 10;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(COLORS.bg);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(W, H);
    container.appendChild(renderer.domElement);

    // ── Stars ──────────────────────────────────────────────────────────────
    const starGeo = new THREE.BufferGeometry();
    const starPos = new Float32Array(STAR_COUNT * 3);
    for (let i = 0; i < STAR_COUNT; i++) {
      starPos[i * 3] = randomBetween(-viewW / 2, viewW / 2);
      starPos[i * 3 + 1] = randomBetween(-viewH / 2, viewH / 2);
      starPos[i * 3 + 2] = 0;
    }
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    const starMat = new THREE.PointsMaterial({ color: COLORS.star, size: 0.08, transparent: true, opacity: 0.7 });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    // ── Player ship ────────────────────────────────────────────────────────
    // Builds the player ship meshes with layered 3D depth so it reads as a lit hull.
    function makePlayer() {
      const g = new THREE.Group();
      const baseColor = new THREE.Color(COLORS.player);
      const darkColor = baseColor.clone().multiplyScalar(0.45);
      const midColor = baseColor.clone().multiplyScalar(0.7);
      const lightColor = baseColor.clone().lerp(new THREE.Color(0xffffff), 0.45);
      const glowColor = baseColor.clone().lerp(new THREE.Color(0x66fff0), 0.35);

      const hullShape = new THREE.Shape();
      hullShape.moveTo(0, 0.9);
      hullShape.lineTo(-0.62, -0.58);
      hullShape.lineTo(0.62, -0.58);
      hullShape.lineTo(0, 0.9);

      const hullGeo = new THREE.ExtrudeGeometry(hullShape, {
        depth: 0.22,
        bevelEnabled: true,
        bevelThickness: 0.05,
        bevelSize: 0.07,
        bevelSegments: 2,
        steps: 1,
      });
      hullGeo.translate(0, 0, -0.11);

      const hullMat = new THREE.MeshBasicMaterial({
        color: baseColor,
        transparent: false,
        opacity: 1,
      });
      const hullShadowMat = new THREE.MeshBasicMaterial({
        color: darkColor,
        transparent: false,
        opacity: 1,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
      const hullHighlightMat = new THREE.MeshBasicMaterial({
        color: lightColor,
        transparent: false,
        opacity: 1,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
      });
      const glowMat = new THREE.MeshBasicMaterial({
        color: glowColor,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      const hull = new THREE.Mesh(hullGeo, hullMat);
      hull.position.z = 0.02;
      g.add(hull);

      const hullShadow = new THREE.Mesh(hullGeo, hullShadowMat);
      hullShadow.scale.set(0.98, 0.98, 0.98);
      hullShadow.position.set(0.02, -0.04, -0.05);
      g.add(hullShadow);

      const hullHighlight = new THREE.Mesh(hullGeo, hullHighlightMat);
      hullHighlight.scale.set(0.93, 0.93, 0.93);
      hullHighlight.position.set(-0.02, 0.05, 0.07);
      g.add(hullHighlight);

      const hullGlow = new THREE.Mesh(hullGeo, glowMat);
      hullGlow.scale.set(1.08, 1.08, 1.08);
      hullGlow.position.z = -0.02;
      g.add(hullGlow);

      const edgeLines = new THREE.LineSegments(
        new THREE.EdgesGeometry(hullGeo, 24),
        new THREE.LineBasicMaterial({ color: lightColor, transparent: true, opacity: 0.5 })
      );
      edgeLines.position.z = 0.08;
      g.add(edgeLines);

      const canopyGeo = new THREE.BoxGeometry(0.26, 0.18, 0.16);
      const canopy = new THREE.Mesh(
        canopyGeo,
        new THREE.MeshBasicMaterial({ color: lightColor, transparent: false, opacity: 1 })
      );
      canopy.position.set(0, 0.32, 0.08);
      g.add(canopy);

      const canopyHighlight = new THREE.Mesh(
        canopyGeo,
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 })
      );
      canopyHighlight.scale.set(0.8, 0.6, 0.8);
      canopyHighlight.position.set(0.04, 0.36, 0.14);
      g.add(canopyHighlight);

      const cannonGeo = new THREE.BoxGeometry(0.12, 0.42, 0.12);
      const cannon = new THREE.Mesh(
        cannonGeo,
        new THREE.MeshBasicMaterial({ color: midColor, transparent: false, opacity: 1 })
      );
      cannon.position.set(0, 0.78, 0.06);
      g.add(cannon);

      const cannonCore = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.38, 0.08),
        new THREE.MeshBasicMaterial({ color: lightColor, transparent: false, opacity: 1 })
      );
      cannonCore.position.set(0, 0.8, 0.1);
      g.add(cannonCore);

      const wingShape = new THREE.Shape();
      wingShape.moveTo(0, -0.05);
      wingShape.lineTo(0.75, -0.35);
      wingShape.lineTo(0.5, 0.18);
      wingShape.lineTo(0, 0.1);

      const wingGeo = new THREE.ExtrudeGeometry(wingShape, {
        depth: 0.12,
        bevelEnabled: true,
        bevelThickness: 0.03,
        bevelSize: 0.04,
        bevelSegments: 2,
        steps: 1,
      });
      wingGeo.translate(0, 0, -0.06);

      [-1, 1].forEach((side) => {
        const wing = new THREE.Mesh(
          wingGeo,
          new THREE.MeshBasicMaterial({ color: midColor, transparent: false, opacity: 1 })
        );
        wing.scale.x = side;
        wing.position.set(side * 0.15, -0.18, -0.02);
        g.add(wing);

        const wingTop = new THREE.Mesh(
          wingGeo,
          new THREE.MeshBasicMaterial({ color: lightColor, transparent: false, opacity: 1 })
        );
        wingTop.scale.set(side * 0.94, 0.94, 0.94);
        wingTop.position.set(side * 0.12, -0.14, 0.04);
        g.add(wingTop);

        const wingEdgeGlow = new THREE.Mesh(
          wingGeo,
          new THREE.MeshBasicMaterial({
            color: glowColor,
            transparent: true,
            opacity: 0.25,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        wingEdgeGlow.scale.set(side * 1.05, 1.05, 1.05);
        wingEdgeGlow.position.set(side * 0.17, -0.2, 0.02);
        g.add(wingEdgeGlow);
      });

      const engineGeo = new THREE.BoxGeometry(0.4, 0.18, 0.16);
      const engine = new THREE.Mesh(
        engineGeo,
        new THREE.MeshBasicMaterial({ color: darkColor, transparent: false, opacity: 1 })
      );
      engine.position.set(0, -0.52, -0.02);
      g.add(engine);

      const engineGlow = new THREE.Mesh(
        new THREE.CircleGeometry(0.16, 18),
        new THREE.MeshBasicMaterial({
          color: glowColor,
          transparent: true,
          opacity: 0.5,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      engineGlow.position.set(0, -0.62, -0.12);
      g.add(engineGlow);

      return g;
    }

    const player = makePlayer();
    player.position.y = -viewH / 2 + 1.5;
    scene.add(player);

    // ── Shields ────────────────────────────────────────────────────────────
    const SHIELD_COLS = 5;
    const SHIELD_ROWS = 3;
    const SHIELD_BLOCK = 0.28;
    const shieldGroups = [];
    const numShields = responsive.shieldCount;
    for (let s = 0; s < numShields; s++) {
      const sx = -viewW / 2 + (viewW / (numShields + 1)) * (s + 1);
      const sy = player.position.y + 2.4;
      const blocks = [];
      for (let r = 0; r < SHIELD_ROWS; r++) {
        for (let c = 0; c < SHIELD_COLS; c++) {
          if (r === 0 && (c === 0 || c === SHIELD_COLS - 1)) continue;
          const geo = new THREE.BoxGeometry(SHIELD_BLOCK, SHIELD_BLOCK, 0.1);
          const mat = new THREE.MeshBasicMaterial({ color: COLORS.shield });
          const m = new THREE.Mesh(geo, mat);
          m.position.set(
            sx + (c - (SHIELD_COLS - 1) / 2) * (SHIELD_BLOCK + 0.04),
            sy + (r - (SHIELD_ROWS - 1) / 2) * (SHIELD_BLOCK + 0.04),
            0
          );
          scene.add(m);
          blocks.push({ mesh: m, hp: 3 });
        }
      }
      shieldGroups.push(blocks);
    }

    // ── Invaders ───────────────────────────────────────────────────────────
    // Build a single invader mesh group with a visible two-eye face and neon glow.
    function makeInvader(row) {
      const g = new THREE.Group();
      const palette = getInvaderPaletteForLevel(lvl);
      const color = row < 1 ? palette.invaderC : row < 2 ? palette.invaderA : palette.invaderB;
      const coreMaterial = new THREE.MeshBasicMaterial({
        color,
        transparent: false,
        opacity: 1,
      });
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: palette.glow,
        transparent: true,
        opacity: 0.55,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      // Body
      const bGeo = new THREE.BoxGeometry(INVADER_W, INVADER_H, 0.1);
      g.add(new THREE.Mesh(bGeo, coreMaterial));
      const bodyGlow = new THREE.Mesh(bGeo, glowMaterial);
      bodyGlow.scale.set(1.08, 1.08, 1);
      g.add(bodyGlow);
      // Eyes
      const eyeMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        depthTest: false,
        depthWrite: false,
      });
      const eyeGroup = new THREE.Group();
      eyeGroup.position.z = 0.14;
      [-0.2, 0.2].forEach(ex => {
        const eGeo = new THREE.BoxGeometry(0.12, 0.12, 0.14);
        const e = new THREE.Mesh(eGeo, eyeMaterial);
        e.position.set(ex, 0.1, 0);
        e.renderOrder = 3;
        eyeGroup.add(e);
      });
      g.add(eyeGroup);
      // Antennae
      [-0.25, 0.25].forEach(ax => {
        const aGeo = new THREE.BoxGeometry(0.06, 0.18, 0.1);
        const a = new THREE.Mesh(aGeo, coreMaterial);
        a.position.set(ax, 0.3, 0);
        g.add(a);
      });
      // Legs
      [-0.28, 0, 0.28].forEach(lx => {
        const lGeo = new THREE.BoxGeometry(0.06, 0.15, 0.1);
        const l = new THREE.Mesh(lGeo, coreMaterial);
        l.position.set(lx, -0.32, 0);
        g.add(l);
      });
      return {
        group: g,
        eyeGroup,
        color,
        alive: true,
        row,
        spinSpeed: randomBetween(0.7, 1.2) * INVADER_SPIN_SPEED * (Math.random() > 0.5 ? 1 : -1),
      };
    }

    const totalW = (responsive.cols - 1) * INVADER_GAP_X;
    const totalH = (responsive.rows - 1) * INVADER_GAP_Y;
    const startX = -totalW / 2;
    const startY = viewH / 2 - 3;

    const invaders = [];
    for (let r = 0; r < responsive.rows; r++) {
      for (let c = 0; c < responsive.cols; c++) {
        const inv = makeInvader(r);
        inv.group.position.set(startX + c * INVADER_GAP_X, startY - r * INVADER_GAP_Y, 0);
        scene.add(inv.group);
        invaders.push(inv);
      }
    }

    // ── Bullets ────────────────────────────────────────────────────────────
    const bullets = []; // { mesh, vy, owner: 'player'|'enemy' }

    function spawnBullet(x, y, vy, owner) {
      const geo = new THREE.BoxGeometry(owner === "player" ? 0.07 : 0.1, 0.45, 0.1);
      const mat = new THREE.MeshBasicMaterial({
        color: owner === "player" ? COLORS.bulletPlayer : COLORS.bulletEnemy,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, 0);
      scene.add(mesh);
      bullets.push({ mesh, vy, owner });
    }

    // ── Particles / explosions ────────────────────────────────────────────
    const particles = []; // { mesh, vx, vy, life, maxLife }

    function spawnExplosion(x, y, color) {
      const count = 10;
      for (let i = 0; i < count; i++) {
        const geo = new THREE.BoxGeometry(0.12, 0.12, 0.1);
        const mat = new THREE.MeshBasicMaterial({ color });
        const m = new THREE.Mesh(geo, mat);
        m.position.set(x, y, 0);
        const angle = (Math.PI * 2 * i) / count;
        const speed = randomBetween(1.5, 4);
        scene.add(m);
        particles.push({ mesh: m, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 0.6, maxLife: 0.6 });
      }
    }

    // ── Mystery ship ──────────────────────────────────────────────────────
    let mystery = null;
    let mysteryTimer = randomBetween(15, 25);

    // Creates the mystery ship with a pulsing glow so it reads as a special target.
    function spawnMystery() {
      const geo = new THREE.BoxGeometry(1.2, 0.5, 0.1);
      const mat = new THREE.MeshBasicMaterial({ color: 0xff0055 });
      const glowMat = new THREE.MeshBasicMaterial({
        color: 0xff66aa,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const group = new THREE.Group();
      const core = new THREE.Mesh(geo, mat);
      const glow = new THREE.Mesh(geo, glowMat);
      glow.scale.set(1.2, 1.2, 1);
      group.add(core);
      group.add(glow);
      const dir = Math.random() > 0.5 ? 1 : -1;
      group.position.set(dir > 0 ? -viewW / 2 - 1 : viewW / 2 + 1, viewH / 2 - 1.5, 0);
      scene.add(group);
      mystery = { group, glow, vx: dir * 5, blinkOffset: Math.random() * Math.PI * 2 };
    }

    // ── Game state ─────────────────────────────────────────────────────────
    const state = {
      playerVx: 0,
      touchActive: false,
      touchTargetX: 0,
      shootDown: false,
      lastShot: 0,
      invaderDir: 1,
      invaderSpeed: INITIAL_INVADER_SPEED * (1 + (lvl - 1) * 0.25),
      invaderTimer: 0,
      enemyShootTimer: randomBetween(0.8, 2),
      score: sc,
      lives: lv,
      level: lvl,
      running: true,
      playerHit: false,
      playerFlash: 0,
      viewW, viewH,
      invaderCols: responsive.cols,
      invaderRows: responsive.rows,
      invaderDrop: INVADER_DROP * responsive.invaderDropScale,
      invaderStep: INVADER_GAP_X * 0.22 * responsive.invaderStepScale,
      invaderIntervalScale: responsive.invaderIntervalScale,
      levelSpeedScale: 1 + (lvl - 1) * 0.1,
    };
    // Sync React HUD with incoming values
    setScore(sc);
    setLives(lv);
    setLevel(lvl);

    // ── Input ──────────────────────────────────────────────────────────────
    const keys = {};
    function onKeyDown(e) {
      keys[e.code] = true;
      if (e.code === "Space" || e.code === "ArrowUp") state.shootDown = true;
      e.preventDefault();
    }
    function onKeyUp(e) {
      keys[e.code] = false;
      if (e.code === "Space" || e.code === "ArrowUp") state.shootDown = false;
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    // ── Resize ─────────────────────────────────────────────────────────────
    function onResize() {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      const a = w / h;
      const newVW = viewH * a;
      camera.left = -newVW / 2;
      camera.right = newVW / 2;
      camera.updateProjectionMatrix();
      state.viewW = newVW;
    }
    window.addEventListener("resize", onResize);

    // ── Main Loop ──────────────────────────────────────────────────────────
    let lastTime = performance.now();

    // Runs the per-frame simulation, movement, and rendering updates.
    function loop() {
      animRef.current = requestAnimationFrame(loop);
      if (!state.running) return;

      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      // Player movement
      let vx = 0;
      if (keys["ArrowLeft"] || keys["KeyA"]) vx = -PLAYER_SPEED;
      if (keys["ArrowRight"] || keys["KeyD"]) vx = PLAYER_SPEED;
      if (state.touchActive) {
        const dxToTarget = state.touchTargetX - player.position.x;
        if (Math.abs(dxToTarget) > 0.08) {
          vx += Math.sign(dxToTarget) * PLAYER_SPEED;
        }
      }
      vx += state.playerVx; // touch
      player.position.x += vx * dt;
      player.position.x = Math.max(-state.viewW / 2 + 0.8, Math.min(state.viewW / 2 - 0.8, player.position.x));

      // Player flash after hit
      if (state.playerFlash > 0) {
        state.playerFlash -= dt;
        player.visible = Math.floor(state.playerFlash * 10) % 2 === 0;
      } else {
        player.visible = true;
      }

      // Shooting
      state.lastShot += dt;
      const wantShoot = keys["Space"] || keys["ArrowUp"] || keys["KeyZ"] || state.shootDown || autoFireRef.current;
      if (wantShoot && state.lastShot > SHOOT_COOLDOWN) {
        state.lastShot = 0;
        spawnBullet(player.position.x, player.position.y + 1, BULLET_SPEED, "player");
      }

      // Spin invaders while keeping their eyes facing the camera.
      function updateInvaderSpin(invader, delta) {
        if (!invader.alive) return;
        invader.group.rotation.y += invader.spinSpeed * delta;
        if (invader.eyeGroup) {
          invader.eyeGroup.lookAt(camera.position);
        }
      }

      // Invader movement
      const aliveInvaders = invaders.filter(i => i.alive);
      aliveInvaders.forEach(invader => updateInvaderSpin(invader, dt));
      if (aliveInvaders.length === 0) {
        // Win level → advance to next level
        state.running = false;
        uiRef.current.level = state.level + 1;
        uiRef.current.score = state.score;
        uiRef.current.lives = state.lives;
        setLevel(state.level + 1);
        setGameState("win");
        return;
      }

      state.invaderTimer += dt;
      const invaderIntervalBase = 0.5 / (1 + state.level * 0.3) * (aliveInvaders.length / (state.invaderCols * state.invaderRows));
      const invaderInterval = Math.max(0.08, (invaderIntervalBase * state.invaderIntervalScale) / state.levelSpeedScale);
      if (state.invaderTimer >= invaderInterval) {
        state.invaderTimer = 0;
        let minX = Infinity, maxX = -Infinity, maxY = -Infinity;
        aliveInvaders.forEach(inv => {
          minX = Math.min(minX, inv.group.position.x);
          maxX = Math.max(maxX, inv.group.position.x);
          maxY = Math.max(maxY, inv.group.position.y);
        });

        let drop = false;
        const edgeMargin = INVADER_W / 2 + 0.3;
        if (state.invaderDir === 1 && maxX + INVADER_W / 2 >= state.viewW / 2 - edgeMargin) {
          state.invaderDir = -1;
          drop = true;
        } else if (state.invaderDir === -1 && minX - INVADER_W / 2 <= -state.viewW / 2 + edgeMargin) {
          state.invaderDir = 1;
          drop = true;
        }

        const moveX = state.invaderDir * state.invaderStep * state.levelSpeedScale;
        const moveY = drop ? -state.invaderDrop * state.levelSpeedScale : 0;

        invaders.forEach(inv => {
          if (!inv.alive) return;
          inv.group.position.x += moveX;
          inv.group.position.y += moveY;
        });

        // Animate legs (wiggle)
        invaders.forEach(inv => {
          if (!inv.alive) return;
          inv.frame = (inv.frame || 0) + 1;
          inv.group.children.forEach((child, i) => {
            if (i > 2) child.position.y = -0.32 + Math.sin(inv.frame * 0.8) * 0.06;
          });
        });

        // Check if invaders reached bottom
        if (maxY < player.position.y + 1) {
          state.running = false;
          setGameState("gameover");
          return;
        }
      }

      // Enemy shooting
      state.enemyShootTimer -= dt;
      if (state.enemyShootTimer <= 0 && aliveInvaders.length > 0) {
        state.enemyShootTimer = randomBetween(0.6, 2.2) / (1 + state.level * 0.15);
        const shooter = aliveInvaders[Math.floor(Math.random() * aliveInvaders.length)];
        spawnBullet(shooter.group.position.x, shooter.group.position.y - INVADER_H / 2 - 0.1, -ENEMY_BULLET_SPEED, "enemy");
      }

      // Mystery ship
      mysteryTimer -= dt;
      if (mysteryTimer <= 0 && !mystery) {
        spawnMystery();
        mysteryTimer = randomBetween(18, 32);
      }
      if (mystery) {
        mystery.group.position.x += mystery.vx * dt;
        const blink = 0.35 + 0.45 * Math.abs(Math.sin(now * 0.004 + mystery.blinkOffset));
        mystery.glow.material.opacity = blink;
        mystery.glow.scale.set(1.1 + blink * 0.4, 1.1 + blink * 0.4, 1);
        if (Math.abs(mystery.group.position.x) > state.viewW / 2 + 2) {
          scene.remove(mystery.group);
          mystery = null;
        }
      }

      // Move bullets
      for (let i = bullets.length - 1; i >= 0; i--) {
        const b = bullets[i];
        b.mesh.position.y += b.vy * dt;

        // Out of bounds
        if (b.mesh.position.y > state.viewH / 2 + 1 || b.mesh.position.y < -state.viewH / 2 - 1) {
          scene.remove(b.mesh);
          bullets.splice(i, 1);
          continue;
        }

        // Hit invader (player bullet)
        if (b.owner === "player") {
          let hit = false;
          for (let j = 0; j < invaders.length; j++) {
            const inv = invaders[j];
            if (!inv.alive) continue;
            const dx = Math.abs(b.mesh.position.x - inv.group.position.x);
            const dy = Math.abs(b.mesh.position.y - inv.group.position.y);
            if (dx < INVADER_W / 2 + 0.1 && dy < INVADER_H / 2 + 0.2) {
              inv.alive = false;
              scene.remove(inv.group);
              spawnExplosion(inv.group.position.x, inv.group.position.y, inv.color);
              playSfxEvent("enemyHit");
              const pts = inv.row === 0 ? 30 : inv.row === 1 ? 20 : 10;
              state.score += pts;
              uiRef.current.score = state.score;
              setScore(state.score);
              if (state.score > uiRef.current.hiScore) {
                uiRef.current.hiScore = state.score;
                setHiScore(state.score);
              }
              scene.remove(b.mesh);
              bullets.splice(i, 1);
              hit = true;
              break;
            }
          }
          if (hit) continue;

          // Hit mystery
          if (mystery) {
            if (!mystery.group || !mystery.group.position) {
              console.warn("Space: mystery target missing group position during hit test", mystery);
            } else {
              const dx = Math.abs(b.mesh.position.x - mystery.group.position.x);
              const dy = Math.abs(b.mesh.position.y - mystery.group.position.y);
              if (dx < 0.8 && dy < 0.4) {
                spawnExplosion(mystery.group.position.x, mystery.group.position.y, 0xff0055);
                state.score += 100;
                uiRef.current.score = state.score;
                setScore(state.score);
                scene.remove(mystery.group);
                mystery = null;
                scene.remove(b.mesh);
                bullets.splice(i, 1);
                continue;
              }
            }
          }

          // Hit shield
          let shieldHit = false;
          for (const group of shieldGroups) {
            for (let k = group.length - 1; k >= 0; k--) {
              const bl = group[k];
              if (!bl.mesh.visible) continue;
              const dx = Math.abs(b.mesh.position.x - bl.mesh.position.x);
              const dy = Math.abs(b.mesh.position.y - bl.mesh.position.y);
              if (dx < 0.2 && dy < 0.2) {
                bl.hp--;
                if (bl.hp <= 0) {
                  bl.mesh.visible = false;
                } else {
                  bl.mesh.material.opacity = bl.hp / 3;
                  bl.mesh.material.transparent = true;
                }
                scene.remove(b.mesh);
                bullets.splice(i, 1);
                shieldHit = true;
                break;
              }
            }
            if (shieldHit) break;
          }
          if (shieldHit) continue;
        }

        // Enemy bullet hit player
        if (b.owner === "enemy" && state.playerFlash <= 0) {
          const dx = Math.abs(b.mesh.position.x - player.position.x);
          const dy = Math.abs(b.mesh.position.y - player.position.y);
          if (dx < 0.6 && dy < 0.6) {
            state.lives--;
            uiRef.current.lives = state.lives;
            setLives(state.lives);
            triggerDeathMessage();
            spawnExplosion(player.position.x, player.position.y, COLORS.player);
            playSfxEvent("playerHit");
            scene.remove(b.mesh);
            bullets.splice(i, 1);
            if (state.lives <= 0) {
              state.running = false;
              setGameState("gameover");
              return;
            }
            state.playerFlash = 2.5;
            continue;
          }
          // Enemy bullet hit shield
          let shieldHit2 = false;
          for (const group of shieldGroups) {
            for (let k = group.length - 1; k >= 0; k--) {
              const bl = group[k];
              if (!bl.mesh.visible) continue;
              const dx2 = Math.abs(b.mesh.position.x - bl.mesh.position.x);
              const dy2 = Math.abs(b.mesh.position.y - bl.mesh.position.y);
              if (dx2 < 0.2 && dy2 < 0.2) {
                bl.hp--;
                if (bl.hp <= 0) bl.mesh.visible = false;
                scene.remove(b.mesh);
                bullets.splice(i, 1);
                shieldHit2 = true;
                break;
              }
            }
            if (shieldHit2) break;
          }
        }
      }

      // Particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life -= dt;
        if (p.life <= 0) {
          scene.remove(p.mesh);
          particles.splice(i, 1);
          continue;
        }
        p.mesh.position.x += p.vx * dt;
        p.mesh.position.y += p.vy * dt;
        p.mesh.material.opacity = p.life / p.maxLife;
        p.mesh.material.transparent = true;
        const s = p.life / p.maxLife;
        p.mesh.scale.set(s, s, 1);
      }

      // Star twinkle
      const starOpacity = 0.4 + 0.3 * Math.sin(now * 0.001);
      starMat.opacity = starOpacity;

      renderer.render(scene, camera);
    }

    loop();

    // Store references for cleanup and touch
    gameRef.current = {
      state,
      renderer,
      cleanup: () => {
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("keyup", onKeyUp);
        window.removeEventListener("resize", onResize);
      },
    };
  }, [playSfxEvent, triggerDeathMessage]);

  // ─── Start / Restart ────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    markUserInteracted();
    setScore(0);
    setLives(3);
    setLevel(1);
    uiRef.current = { score: 0, lives: 3, level: 1, hiScore: uiRef.current.hiScore };
    setGameState("playing");
    setTimeout(() => initScene({ lvl: 1, sc: 0, lv: 3 }), 50);
  }, [initScene]);

  const nextLevel = useCallback(() => {
    markUserInteracted();
    const lvl = uiRef.current.level;
    const sc = uiRef.current.score;
    const lv = uiRef.current.lives;
    setGameState("playing");
    setTimeout(() => initScene({ lvl, sc, lv }), 50);
  }, [initScene]);

  // ─── Touch controls ─────────────────────────────────────────────────────────
  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    // Converts a touch X coordinate into world-space and updates the movement target.
    function updateTouchTarget(clientX) {
      if (!gameRef.current) return;
      const rect = el.getBoundingClientRect();
      if (!rect.width) return;
      const normalizedX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const targetX = normalizedX * (gameRef.current.state.viewW / 2);
      const minX = -gameRef.current.state.viewW / 2 + 0.8;
      const maxX = gameRef.current.state.viewW / 2 - 0.8;
      gameRef.current.state.touchTargetX = Math.max(minX, Math.min(maxX, targetX));
    }

    function onTouchStart(e) {
      if (gameState !== "playing" || !gameRef.current) return;
      const t = e.changedTouches[0];
      if (!t) return;
      touchRef.current = { startX: t.clientX, startY: t.clientY, touchId: t.identifier };
      gameRef.current.state.touchActive = true;
      updateTouchTarget(t.clientX);
      if (e.cancelable) e.preventDefault();
    }

    function onTouchMove(e) {
      if (gameState !== "playing" || !gameRef.current) return;
      const touches = Array.from(e.changedTouches || []);
      const activeTouch = touches.find((t) => t.identifier === touchRef.current.touchId) || e.touches[0];
      if (!activeTouch) return;
      if (gameRef.current.state.touchActive) {
        updateTouchTarget(activeTouch.clientX);
      }
      if (e.cancelable) e.preventDefault();
    }

    function onTouchEnd(e) {
      if (!gameRef.current) return;
      gameRef.current.state.playerVx = 0;
      gameRef.current.state.touchActive = false;
      // tap to shoot
      if (e.changedTouches[0]) {
        const dx = e.changedTouches[0].clientX - touchRef.current.startX;
        const dy = e.changedTouches[0].clientY - touchRef.current.startY;
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          gameRef.current.state.shootDown = true;
          setTimeout(() => {
            if (gameRef.current) gameRef.current.state.shootDown = false;
          }, 100);
        }
      }
      touchRef.current.touchId = null;
      if (e.cancelable) e.preventDefault();
    }

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [gameState]);

  // Listens for the first user gesture to unlock audio playback.
  useEffect(() => {
    function onFirstGesture() {
      markUserInteracted();
    }
    window.addEventListener("pointerdown", onFirstGesture);
    window.addEventListener("keydown", onFirstGesture);
    window.addEventListener("touchstart", onFirstGesture, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
  }, [markUserInteracted]);

  // ─── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (gameRef.current) gameRef.current.cleanup();
      if (sfxRef.current) {
        sfxRef.current.dispose?.();
        sfxRef.current = null;
      }
      if (deathMessageTimerRef.current) {
        clearTimeout(deathMessageTimerRef.current);
        deathMessageTimerRef.current = null;
      }
    };
  }, []);

  // ─── Overlay ─────────────────────────────────────────────────────────────────
  const HeartIcons = ({ count }) => (
    <span style={{ fontSize: "clamp(14px,3vw,20px)", letterSpacing: 2 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <span key={i} style={{ color: i < count ? COLORS.hudAlt : "#333", marginRight: 2 }}>♥</span>
      ))}
    </span>
  );

  return (
    <div style={{ width: "100vw", height: "100dvh", background: "#000510", fontFamily: "'Courier New', monospace", overflow: "hidden", position: "relative", userSelect: "none" }}>
      {/* Three.js canvas */}
      <div ref={mountRef} style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }} />

      {/* HUD */}
      {gameState === "playing" && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", pointerEvents: "none", zIndex: 10 }}>
          <div style={{ color: COLORS.hud, fontSize: "clamp(11px,2.5vw,16px)", textShadow: `0 0 8px ${COLORS.hud}` }}>
            <div style={{ opacity: 0.6, fontSize: "0.75em", letterSpacing: 2 }}>SCORE</div>
            <div style={{ fontWeight: "bold", fontSize: "1.2em" }}>{score}</div>
          </div>
          <div style={{ color: COLORS.hud, fontSize: "clamp(11px,2.5vw,16px)", textAlign: "center", textShadow: `0 0 8px ${COLORS.hud}` }}>
            <div style={{ opacity: 0.6, fontSize: "0.75em", letterSpacing: 2 }}>LEVEL</div>
            <div style={{ fontWeight: "bold", fontSize: "1.2em" }}>{level}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, color: COLORS.hud, textAlign: "right", fontSize: "clamp(11px,2.5vw,16px)" }}>
            <div>
              <div style={{ opacity: 0.6, fontSize: "0.75em", letterSpacing: 2, textShadow: `0 0 8px ${COLORS.hudAlt}` }}>LIVES</div>
              <HeartIcons count={lives} />
            </div>
            <AutoFireToggle
              enabled={autoFireEnabled}
              onToggle={handleAutoFireToggle}
            />
            <AudioToggleGroup
              isSoundtrackMuted={isSoundtrackMuted}
              isAllMuted={isAllMuted}
              onToggleSoundtrack={toggleSoundtrackMute}
              onToggleAll={toggleAllMute}
            />
          </div>
        </div>
      )}

      {/* Touch hint */}
      {gameState === "playing" && (
        <div style={{ position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center", color: "rgba(0,255,224,0.25)", fontSize: "clamp(9px,2vw,12px)", pointerEvents: "none", zIndex: 10, letterSpacing: 2 }}>
          TOUCH TO MOVE • TAP TO FIRE
        </div>
      )}

      {/* Life loss message */}
      {showDeathMessage && gameState === "playing" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 15 }}>
          <div style={{ color: "#ff3c6e", fontSize: "clamp(24px,8vw,64px)", letterSpacing: "0.2em", textTransform: "uppercase", textShadow: "0 0 12px #ff3c6e, 0 0 32px #ff3c6e", opacity: 0.95, transition: "opacity 0.4s ease" }}>
            you're dead!
          </div>
        </div>
      )}

      {/* Idle / splash screen */}
      {gameState === "idle" && (
        <Overlay>
          <GlowTitle>GALAXY ASSAULT</GlowTitle>
          <Subtitle>ALIEN WARS</Subtitle>
          <ScoreLine label="HI-SCORE" value={hiScore} />
          <Spacer />
          <InvaderLegend />
          <Spacer />
          <StartBtn onClick={startGame}>PRESS START</StartBtn>
          <AutoFireToggle
            enabled={autoFireEnabled}
            onToggle={handleAutoFireToggle}
          />
          <AudioToggleGroup
            isSoundtrackMuted={isSoundtrackMuted}
            isAllMuted={isAllMuted}
            onToggleSoundtrack={toggleSoundtrackMute}
            onToggleAll={toggleAllMute}
          />
          <ControlsHint />
        </Overlay>
      )}

      {/* Game over */}
      {gameState === "gameover" && (
        <Overlay>
          <GlowTitle color="#ff3c6e">GAME OVER</GlowTitle>
          <ScoreLine label="SCORE" value={score} />
          <ScoreLine label="HI-SCORE" value={Math.max(score, hiScore)} />
          <Spacer />
          <StartBtn onClick={startGame}>PLAY AGAIN</StartBtn>
          <AutoFireToggle
            enabled={autoFireEnabled}
            onToggle={handleAutoFireToggle}
          />
          <AudioToggleGroup
            isSoundtrackMuted={isSoundtrackMuted}
            isAllMuted={isAllMuted}
            onToggleSoundtrack={toggleSoundtrackMute}
            onToggleAll={toggleAllMute}
          />
        </Overlay>
      )}

      {/* Win */}
      {gameState === "win" && (
        <Overlay>
          <GlowTitle color="#ffb800">SECTOR CLEAR</GlowTitle>
          <div style={{ color: "#ffb800", fontFamily: "'Courier New', monospace", fontSize: "clamp(13px,3vw,18px)", letterSpacing: 4, marginBottom: 8 }}>
            LEVEL {level} COMPLETE
          </div>
          <ScoreLine label="SCORE" value={score} />
          <Spacer />
          <StartBtn onClick={nextLevel}>NEXT WAVE</StartBtn>
          <AutoFireToggle
            enabled={autoFireEnabled}
            onToggle={handleAutoFireToggle}
          />
          <AudioToggleGroup
            isSoundtrackMuted={isSoundtrackMuted}
            isAllMuted={isAllMuted}
            onToggleSoundtrack={toggleSoundtrackMute}
            onToggleAll={toggleAllMute}
          />
        </Overlay>
      )}
    </div>
  );
}

// ─── UI sub-components ────────────────────────────────────────────────────────

function Overlay({ children }) {
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 20,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "rgba(0,5,16,0.82)", backdropFilter: "blur(2px)",
      padding: "20px",
    }}>
      {children}
    </div>
  );
}

function GlowTitle({ children, color = "#00ffe0" }) {
  return (
    <h1 style={{
      margin: "0 0 4px",
      fontSize: "clamp(40px,12vw,96px)",
      fontFamily: "'Courier New', monospace",
      fontWeight: 900,
      letterSpacing: "0.12em",
      color,
      textShadow: `0 0 20px ${color}, 0 0 60px ${color}88`,
      lineHeight: 1,
    }}>{children}</h1>
  );
}

function Subtitle({ children }) {
  return (
    <p style={{
      margin: "0 0 24px",
      fontSize: "clamp(10px,3vw,16px)",
      letterSpacing: "0.5em",
      color: "#ffffff88",
      fontFamily: "'Courier New', monospace",
    }}>{children}</p>
  );
}

function ScoreLine({ label, value }) {
  return (
    <div style={{ fontFamily: "'Courier New', monospace", color: "#fff", fontSize: "clamp(13px,3vw,18px)", margin: "4px 0", letterSpacing: 3 }}>
      <span style={{ color: "#ffffff55" }}>{label}: </span>
      <span style={{ color: "#ffb800" }}>{value}</span>
    </div>
  );
}

function Spacer() {
  return <div style={{ height: "clamp(16px,4vh,32px)" }} />;
}

function StartBtn({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "2px solid #00ffe0",
        color: "#00ffe0",
        fontFamily: "'Courier New', monospace",
        fontSize: "clamp(13px,3.5vw,20px)",
        letterSpacing: "0.3em",
        padding: "12px 36px",
        cursor: "pointer",
        boxShadow: "0 0 18px #00ffe044, inset 0 0 18px #00ffe011",
        transition: "all .2s",
        marginBottom: 12,
      }}
      onMouseEnter={e => { e.target.style.background = "#00ffe022"; e.target.style.boxShadow = "0 0 30px #00ffe088"; }}
      onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.boxShadow = "0 0 18px #00ffe044, inset 0 0 18px #00ffe011"; }}
    >{children}</button>
  );
}

// Renders grouped audio toggles for soundtrack-only and global mute control.
function AudioToggleGroup({ isSoundtrackMuted, isAllMuted, onToggleSoundtrack, onToggleAll }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <AudioToggleButton
        isActive={isSoundtrackMuted}
        onToggle={onToggleSoundtrack}
        labelOn="UNMUTE MUSIC"
        labelOff="MUTE MUSIC"
      />
      <AudioToggleButton
        isActive={isAllMuted}
        onToggle={onToggleAll}
        labelOn="UNMUTE ALL"
        labelOff="MUTE ALL"
      />
    </div>
  );
}

// Renders a single audio toggle button with consistent styling.
function AudioToggleButton({ isActive, onToggle, labelOn, labelOff }) {
  return (
    <button
      onClick={onToggle}
      style={{
        background: "transparent",
        border: "1px solid #00ffe0",
        color: "#00ffe0",
        fontFamily: "'Courier New', monospace",
        fontSize: "clamp(10px,2.5vw,14px)",
        letterSpacing: "0.2em",
        padding: "6px 10px",
        cursor: "pointer",
        boxShadow: "0 0 10px #00ffe044, inset 0 0 10px #00ffe011",
        pointerEvents: "auto",
      }}
    >
      {isActive ? labelOn : labelOff}
    </button>
  );
}

// Renders the auto-fire checkbox toggle with the same retro HUD styling.
function AutoFireToggle({ enabled, onToggle }) {
  return (
    <label style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: "#00ffe0",
      fontSize: "clamp(10px,2.5vw,14px)",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      textShadow: "0 0 8px #00ffe0",
      pointerEvents: "auto",
    }}>
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        style={{ accentColor: "#00ffe0" }}
      />
      Auto-fire
    </label>
  );
}

function InvaderLegend() {
  const items = [
    { color: "#b44fff", pts: "30 PTS", label: "▲" },
    { color: "#ff3c6e", pts: "20 PTS", label: "■" },
    { color: "#ffb800", pts: "10 PTS", label: "●" },
    { color: "#ff0055", pts: "100 PTS [Mystery Ship]", label: "◆" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", marginBottom: 8 }}>
      {items.map((item) => (
        <div key={item.label} style={{ fontFamily: "'Courier New', monospace", fontSize: "clamp(11px,2.5vw,14px)", display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ color: item.color, fontSize: "1.3em", width: 80, textAlign: "right" }}>{item.label}</span>
          <span style={{ color: "#ffffff88" }}>= {item.pts}</span>
        </div>
      ))}
    </div>
  );
}

function ControlsHint() {
  return (
    <div style={{
      marginTop: 20,
      fontFamily: "'Courier New', monospace",
      fontSize: "clamp(9px,2vw,12px)",
      color: "#ffffff33",
      letterSpacing: "0.2em",
      textAlign: "center",
      lineHeight: 1.8,
    }}>
      ← → MOVE &nbsp;|&nbsp; SPACE / ↑ FIRE<br />
      MOBILE: TOUCH &amp; TAP
    </div>
  );
}
